import {
  CheckCircle,
  Close,
  Search,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { EmployeePersona } from "../../../types";
import { hostname, addImageDimensions } from "../../../utils";
import { useEmployeeTerminology } from "../../../utils/terminology";

const TeamSelectionContainer = styled(Box)(({ theme }) => ({
  width: "100%",
}));

const SelectedTeamBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  minHeight: 80,
  flexWrap: "wrap",
}));

const PersonaCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  height: "100%",
  transition: "all 0.2s",
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : `1px solid ${theme.palette.divider}`,
  backgroundColor: selected
    ? theme.palette.action.selected
    : theme.palette.background.paper,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[2],
  },
}));

interface TeamSelectionStepProps {
  selectedPersonas: EmployeePersona[];
  onSelectPersonas: (personas: EmployeePersona[]) => void;
  onNext: () => void;
  onBack: () => void;
  loading?: boolean;
  appName: string;
  getToken: () => Promise<string>;
  minSelections?: number;
}

export default function TeamSelectionStep({
  selectedPersonas,
  onSelectPersonas,
  onNext,
  onBack,
  loading = false,
  appName,
  getToken,
  minSelections = 3,
}: TeamSelectionStepProps) {
  const { employee: employeeText, employees: employeesText } = useEmployeeTerminology();
  const [personas, setPersonas] = useState<EmployeePersona[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchPersonas();
    fetchCategories();
  }, []);

  const fetchPersonas = async () => {
    setLoadingPersonas(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (selectedCategory) {
        params.append("category", selectedCategory);
      }
      const response = await axios.get(
        `${hostname}/employee-personas${params.toString() ? `?${params}` : ""}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPersonas(response.data.personas || []);
    } catch (error) {
      console.error("Error fetching personas:", error);
    } finally {
      setLoadingPersonas(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/employee-personas/categories`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchPersonas();
  }, [selectedCategory]);

  const filteredPersonas = personas.filter((persona) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      persona.title.toLowerCase().includes(query) ||
      persona.name?.toLowerCase().includes(query) ||
      persona.description?.toLowerCase().includes(query) ||
      persona.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  const handleTogglePersona = (persona: EmployeePersona) => {
    const isSelected = selectedPersonas.some((p) => p.id === persona.id);

    if (isSelected) {
      // Remove from selection
      onSelectPersonas(selectedPersonas.filter((p) => p.id !== persona.id));
    } else {
      // Add to selection (no max limit)
      onSelectPersonas([...selectedPersonas, persona]);
    }
  };

  const handleRemovePersona = (personaId: string) => {
    onSelectPersonas(selectedPersonas.filter((p) => p.id !== personaId));
  };

  const isSelected = (personaId: string) =>
    selectedPersonas.some((p) => p.id === personaId);

  return (
    <TeamSelectionContainer>
      <Fade in={true} timeout={800}>
        <Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "text.primary",
              }}
            >
              Build Your AI Team
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              Select at least {minSelections} AI {employeesText.toLowerCase()} to get started.
              You can always add or remove them later.
            </Typography>
          </Box>

          {/* Selected Team Bar */}
          <SelectedTeamBar>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 1, fontWeight: 500 }}
            >
              Your team ({selectedPersonas.length}):
            </Typography>
            {selectedPersonas.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Click on {employeesText.toLowerCase()} below to add them to your team
              </Typography>
            ) : (
              selectedPersonas.map((persona) => (
                <Chip
                  key={persona.id}
                  avatar={
                    <Avatar
                      src={
                        persona.picture
                          ? addImageDimensions(persona.picture, 48, 48)
                          : undefined
                      }
                    >
                      {persona.title?.charAt(0)}
                    </Avatar>
                  }
                  label={persona.name || persona.title}
                  onDelete={() => handleRemovePersona(persona.id)}
                  deleteIcon={<Close fontSize="small" />}
                  color="primary"
                  variant="outlined"
                />
              ))
            )}
          </SelectedTeamBar>

          {/* Search and Filter */}
          <Stack spacing={2} sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder={`Search ${employeesText.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ pb: 1 }}
            >
              <Chip
                label="All"
                variant={selectedCategory === null ? "filled" : "outlined"}
                color={selectedCategory === null ? "primary" : "default"}
                onClick={() => setSelectedCategory(null)}
                size="small"
              />
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  variant={selectedCategory === category ? "filled" : "outlined"}
                  color={selectedCategory === category ? "primary" : "default"}
                  onClick={() => setSelectedCategory(category)}
                  size="small"
                />
              ))}
            </Stack>
          </Stack>

          {/* Persona Grid */}
          <Box
            sx={{
              minHeight: 400,
              maxHeight: "calc(100vh - 520px)",
              overflow: "auto",
              mb: 3,
              pr: 1,
            }}
          >
            {loadingPersonas ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredPersonas.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  {searchQuery
                    ? "No results match your search"
                    : `No ${employeesText.toLowerCase()} available yet`}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredPersonas.map((persona) => {
                  const selected = isSelected(persona.id);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={persona.id}>
                      <PersonaCard
                        variant="outlined"
                        selected={selected}
                      >
                        <CardActionArea
                          onClick={() => handleTogglePersona(persona)}
                          sx={{ height: "100%", p: 2 }}
                        >
                          <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box sx={{ position: "relative" }}>
                              <Avatar
                                src={
                                  persona.picture
                                    ? addImageDimensions(persona.picture, 120, 120)
                                    : undefined
                                }
                                sx={{ width: 60, height: 60, flexShrink: 0 }}
                              >
                                {persona.title?.charAt(0)}
                              </Avatar>
                              {selected && (
                                <CheckCircle
                                  color="primary"
                                  sx={{
                                    position: "absolute",
                                    bottom: -4,
                                    right: -4,
                                    backgroundColor: "background.paper",
                                    borderRadius: "50%",
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {persona.title}
                              </Typography>
                              {persona.name && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                >
                                  {persona.name}
                                </Typography>
                              )}
                              {persona.category && (
                                <Chip
                                  label={persona.category}
                                  size="small"
                                  sx={{ mt: 0.5, fontSize: "0.7rem", height: 20 }}
                                />
                              )}
                            </Box>
                          </Stack>
                        </CardActionArea>
                      </PersonaCard>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={onBack}
              disabled={loading}
              sx={{ px: 3 }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={onNext}
              disabled={loading || selectedPersonas.length < minSelections}
              sx={{
                px: 4,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : null}
              Continue with {selectedPersonas.length} {selectedPersonas.length === 1 ? employeeText : employeesText}
            </Button>
          </Box>

          {selectedPersonas.length < minSelections && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 2 }}
            >
              Select at least {minSelections} {minSelections === 1 ? employeeText.toLowerCase() : employeesText.toLowerCase()} to continue
            </Typography>
          )}
        </Box>
      </Fade>
    </TeamSelectionContainer>
  );
}
